import React, { useMemo, useState } from 'react';
import {
  FaPlus,
  FaTrash,
  FaCheckCircle,
  FaArrowUp,
  FaArrowDown,
} from 'react-icons/fa';
import DiscordEmbedPreview from './DiscordEmbedPreview';
import './WelcomeSettingsPanel.css';

const ICON_MODES = [
  { value: 'user', label: 'Avatar do membro' },
  { value: 'server', label: 'Ícone do servidor' },
  { value: 'bot', label: 'Avatar do bot' },
  { value: 'custom', label: 'URL personalizada' },
  { value: 'none', label: 'Nenhum' },
];

const BUTTON_STYLES = [
  { value: 'Primary', label: 'Azul (Primary)' },
  { value: 'Secondary', label: 'Cinza (Secondary)' },
  { value: 'Success', label: 'Verde (Success)' },
  { value: 'Danger', label: 'Vermelho (Danger)' },
];

const MAX_BUTTONS_DEFAULT = 5;

const emptyField = () => ({
  name: 'Novo campo',
  value: 'Conteúdo do campo',
  inline: false,
});

function createEmptyButton() {
  return {
    id: `btn_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    enabled: true,
    label: 'Novo botão',
    style: 'Primary',
    emoji: '',
    url: '',
    replyTitle: 'Informação',
    replyDescription: 'Conteúdo da resposta ao clicar…',
    replyColor: '#5865F2',
  };
}

/**
 * Painel completo de Boas-vindas inteligentes.
 */
const WelcomeSettingsPanel = ({
  settings,
  templates = [],
  placeholders = [],
  maxButtons = MAX_BUTTONS_DEFAULT,
  guildChannels = [],
  guildResourcesLoading = false,
  saving = false,
  message = '',
  canEdit = true,
  onChange,
  onSave,
  onApplyTemplate,
}) => {
  const [activeButtonId, setActiveButtonId] = useState(null);
  const [templateBusy, setTemplateBusy] = useState(null);
  const [section, setSection] = useState('geral');

  const embed = settings?.embed || {};
  const buttons = Array.isArray(settings?.buttons) ? settings.buttons : [];
  const buttonLimit = maxButtons || MAX_BUTTONS_DEFAULT;

  const SECTIONS = [
    { id: 'geral', label: 'Geral' },
    { id: 'templates', label: 'Templates' },
    { id: 'embed', label: 'Embed' },
    { id: 'botoes', label: `Botões (${buttons.length})` },
  ];

  const selectedId =
    activeButtonId && buttons.some((b) => b.id === activeButtonId)
      ? activeButtonId
      : buttons[0]?.id || null;

  const selectedButton = buttons.find((b) => b.id === selectedId) || null;
  const selectedIndex = buttons.findIndex((b) => b.id === selectedId);

  const updateRoot = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  const updateEmbed = (key, value) => {
    onChange({
      ...settings,
      embed: { ...embed, [key]: value },
    });
  };

  const updateField = (index, key, value) => {
    const fields = (embed.fields || []).map((f, i) =>
      i === index ? { ...f, [key]: value } : f
    );
    updateEmbed('fields', fields);
  };

  const addField = () => {
    if ((embed.fields || []).length >= 25) return;
    updateEmbed('fields', [...(embed.fields || []), emptyField()]);
  };

  const removeField = (index) => {
    updateEmbed(
      'fields',
      (embed.fields || []).filter((_, i) => i !== index)
    );
  };

  const setButtons = (next) => {
    onChange({ ...settings, buttons: next });
  };

  const updateButtonAt = (index, patch) => {
    setButtons(buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  };

  const addButton = () => {
    if (buttons.length >= buttonLimit) return;
    const btn = createEmptyButton();
    setButtons([...buttons, btn]);
    setActiveButtonId(btn.id);
  };

  const removeButton = (index) => {
    const removed = buttons[index];
    const next = buttons.filter((_, i) => i !== index);
    setButtons(next);
    if (removed?.id === selectedId) {
      setActiveButtonId(next[Math.max(0, index - 1)]?.id || null);
    }
  };

  const moveButton = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= buttons.length) return;
    const next = [...buttons];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setButtons(next);
  };

  const insertPlaceholder = (token, target) => {
    if (target === 'title') {
      updateEmbed('title', `${embed.title || ''}${token}`);
      return;
    }
    if (target === 'description') {
      updateEmbed('description', `${embed.description || ''}${token}`);
      return;
    }
    if (target === 'footer') {
      updateEmbed('footer', `${embed.footer || ''}${token}`);
      return;
    }
    if (target === 'btnReply' && selectedIndex >= 0) {
      const current = buttons[selectedIndex]?.replyDescription || '';
      updateButtonAt(selectedIndex, { replyDescription: `${current}${token}` });
    }
  };

  const handleApplyTemplate = async (templateId) => {
    setTemplateBusy(templateId);
    try {
      await onApplyTemplate(templateId);
    } finally {
      setTemplateBusy(null);
    }
  };

  const placeholderChips = useMemo(() => placeholders || [], [placeholders]);

  if (!settings) return null;

  return (
    <section className="admin-section welcome-panel">
      <header className="admin-section__head">
        <h1>Boas-vindas inteligentes</h1>
        <p>
          Embed personalizada + botões de onboarding quando um membro entra no
          Discord
        </p>
      </header>

      {message && <p className="admin-success">{message}</p>}

      <div className="welcome-shell">
        <div className="welcome-editor">
          <nav className="welcome-tabs" aria-label="Seções de boas-vindas">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`welcome-tab${section === s.id ? ' is-active' : ''}`}
                onClick={() => setSection(s.id)}
              >
                {s.label}
              </button>
            ))}
          </nav>

          <div className="welcome-editor__body admin-form">
            {section === 'geral' && (
              <div className="welcome-section-pane">
                <label className="admin-switch">
                  <span>
                    <strong>Sistema ativo</strong>
                    <small>
                      Quando ligado, o bot envia a mensagem no canal escolhido a
                      cada novo membro.
                    </small>
                  </span>
                  <input
                    type="checkbox"
                    checked={Boolean(settings.enabled)}
                    onChange={(e) => updateRoot('enabled', e.target.checked)}
                  />
                </label>

                <label>
                  Canal de boas-vindas
                  <select
                    value={settings.channelId || ''}
                    onChange={(e) =>
                      updateRoot('channelId', e.target.value || null)
                    }
                    disabled={guildResourcesLoading}
                  >
                    <option value="">
                      {guildResourcesLoading
                        ? 'Carregando canais…'
                        : 'Selecione um canal de texto'}
                    </option>
                    {guildChannels.map((ch) => (
                      <option key={ch.id} value={ch.id}>
                        {ch.parentName
                          ? `#${ch.name} (${ch.parentName})`
                          : `#${ch.name}`}
                      </option>
                    ))}
                    {settings.channelId &&
                      !guildChannels.some(
                        (ch) => ch.id === String(settings.channelId)
                      ) && (
                        <option value={settings.channelId}>
                          Canal salvo ({settings.channelId})
                        </option>
                      )}
                  </select>
                </label>

                <div className="welcome-toggle-grid">
                  <label className="admin-switch">
                    <span>
                      <strong>Mencionar o membro</strong>
                      <small>Envia @menção acima da embed.</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(settings.mentionUser)}
                      onChange={(e) =>
                        updateRoot('mentionUser', e.target.checked)
                      }
                    />
                  </label>

                  <label className="admin-switch">
                    <span>
                      <strong>Ignorar bots</strong>
                      <small>Não enviar boas-vindas para contas bot.</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(settings.ignoreBots)}
                      onChange={(e) =>
                        updateRoot('ignoreBots', e.target.checked)
                      }
                    />
                  </label>
                </div>
              </div>
            )}

            {section === 'templates' && (
              <div className="welcome-section-pane">
                <p className="admin-field-hint welcome-hint">
                  Escolha um modelo. Depois você pode ajustar título, campos e
                  botões à vontade.
                </p>
                <div className="welcome-templates">
                  {templates.map((tpl) => {
                    const active = settings.templateId === tpl.id;
                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        className={`welcome-template${active ? ' is-active' : ''}`}
                        style={{ '--tpl-accent': tpl.accent || '#7C3AED' }}
                        disabled={Boolean(templateBusy)}
                        onClick={() => handleApplyTemplate(tpl.id)}
                      >
                        <div className="welcome-template__swatch" aria-hidden="true">
                          <span className="welcome-template__bar" />
                          <span className="welcome-template__lines">
                            <i />
                            <i />
                            <i />
                          </span>
                        </div>
                        <header>
                          <div>
                            <p className="welcome-template__tag">{tpl.tagline}</p>
                            <h3>{tpl.name}</h3>
                          </div>
                          {active && (
                            <span className="welcome-template__badge">
                              <FaCheckCircle aria-hidden="true" /> Ativo
                            </span>
                          )}
                        </header>
                        <p className="welcome-template__desc">
                          {tpl.description}
                        </p>
                        <span className="welcome-template__action">
                          {templateBusy === tpl.id
                            ? 'Aplicando…'
                            : active
                              ? 'Reaplicar modelo'
                              : 'Aplicar modelo'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {section === 'embed' && (
              <div className="welcome-section-pane">
                <p className="admin-field-hint welcome-hint">
                  Clique para inserir no título (ou use nos campos manualmente).
                </p>
                <div className="welcome-placeholders">
                  {placeholderChips.map((ph) => (
                    <button
                      key={ph.token}
                      type="button"
                      className="welcome-ph"
                      title={`${ph.label} — ex.: ${ph.example}`}
                      onClick={() => insertPlaceholder(ph.token, 'title')}
                    >
                      <code>{ph.token}</code>
                      <span>{ph.label}</span>
                    </button>
                  ))}
                </div>

                <label>
                  Título
                  <input
                    type="text"
                    value={embed.title || ''}
                    maxLength={256}
                    onChange={(e) => updateEmbed('title', e.target.value)}
                    placeholder="Bem-vindo(a) ao {server}!"
                  />
                </label>

                <label>
                  Descrição
                  <textarea
                    rows={6}
                    value={embed.description || ''}
                    maxLength={4096}
                    onChange={(e) => updateEmbed('description', e.target.value)}
                    placeholder="Olá {user}…"
                  />
                  <div className="welcome-ph-row">
                    {placeholderChips.slice(0, 6).map((ph) => (
                      <button
                        key={`desc-${ph.token}`}
                        type="button"
                        className="welcome-ph welcome-ph--mini"
                        onClick={() =>
                          insertPlaceholder(ph.token, 'description')
                        }
                      >
                        {ph.token}
                      </button>
                    ))}
                  </div>
                </label>

                <div className="welcome-grid-2">
                  <label>
                    Cor da barra
                    <div className="welcome-color">
                      <input
                        type="color"
                        value={
                          /^#[0-9A-Fa-f]{6}$/.test(embed.color || '')
                            ? embed.color
                            : '#7C3AED'
                        }
                        onChange={(e) => updateEmbed('color', e.target.value)}
                        aria-label="Cor da embed"
                      />
                      <input
                        type="text"
                        value={embed.color || ''}
                        onChange={(e) => updateEmbed('color', e.target.value)}
                        placeholder="#7C3AED"
                      />
                    </div>
                  </label>

                  <label className="admin-switch welcome-switch-inline">
                    <span>
                      <strong>Timestamp</strong>
                      <small>Mostra data/hora no rodapé</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(embed.timestamp)}
                      onChange={(e) =>
                        updateEmbed('timestamp', e.target.checked)
                      }
                    />
                  </label>
                </div>

                <details className="welcome-accordion">
                  <summary>Autor, mídia e rodapé</summary>
                  <div className="welcome-accordion__body">
                    <label>
                      Autor (opcional)
                      <input
                        type="text"
                        value={embed.authorName || ''}
                        maxLength={256}
                        onChange={(e) =>
                          updateEmbed('authorName', e.target.value)
                        }
                        placeholder="{server}"
                      />
                    </label>

                    <div className="welcome-grid-2">
                      <label>
                        Ícone do autor
                        <select
                          value={embed.authorIconMode || 'none'}
                          onChange={(e) =>
                            updateEmbed('authorIconMode', e.target.value)
                          }
                        >
                          {ICON_MODES.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      {embed.authorIconMode === 'custom' && (
                        <label>
                          URL do ícone do autor
                          <input
                            type="url"
                            value={embed.authorIconUrl || ''}
                            onChange={(e) =>
                              updateEmbed('authorIconUrl', e.target.value)
                            }
                            placeholder="https://…"
                          />
                        </label>
                      )}
                    </div>

                    <div className="welcome-grid-2">
                      <label>
                        Thumbnail
                        <select
                          value={embed.thumbnailMode || 'user'}
                          onChange={(e) =>
                            updateEmbed('thumbnailMode', e.target.value)
                          }
                        >
                          {ICON_MODES.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      {embed.thumbnailMode === 'custom' && (
                        <label>
                          URL da thumbnail
                          <input
                            type="url"
                            value={embed.thumbnailUrl || ''}
                            onChange={(e) =>
                              updateEmbed('thumbnailUrl', e.target.value)
                            }
                            placeholder="https://… ou {avatar}"
                          />
                        </label>
                      )}
                    </div>

                    <label>
                      Imagem / banner (URL)
                      <input
                        type="url"
                        value={embed.imageUrl || ''}
                        onChange={(e) =>
                          updateEmbed('imageUrl', e.target.value)
                        }
                        placeholder="https://… (opcional)"
                      />
                    </label>

                    <label>
                      Rodapé
                      <input
                        type="text"
                        value={embed.footer || ''}
                        maxLength={2048}
                        onChange={(e) => updateEmbed('footer', e.target.value)}
                        placeholder="{server} • Seja bem-vindo(a)!"
                      />
                    </label>

                    <div className="welcome-grid-2">
                      <label>
                        Ícone do rodapé
                        <select
                          value={embed.footerIconMode || 'server'}
                          onChange={(e) =>
                            updateEmbed('footerIconMode', e.target.value)
                          }
                        >
                          {ICON_MODES.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      {embed.footerIconMode === 'custom' && (
                        <label>
                          URL do ícone do rodapé
                          <input
                            type="url"
                            value={embed.footerIconUrl || ''}
                            onChange={(e) =>
                              updateEmbed('footerIconUrl', e.target.value)
                            }
                            placeholder="https://…"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </details>

                <details className="welcome-accordion">
                  <summary>Campos da embed</summary>
                  <div className="welcome-accordion__body">
                    <div className="welcome-fields">
                      {(embed.fields || []).map((field, index) => (
                        <div key={`field-${index}`} className="welcome-field">
                          <div className="welcome-field__head">
                            <strong>Campo {index + 1}</strong>
                            <button
                              type="button"
                              className="admin-btn admin-btn--danger admin-btn--sm"
                              onClick={() => removeField(index)}
                              aria-label="Remover campo"
                            >
                              <FaTrash aria-hidden="true" />
                            </button>
                          </div>
                          <label>
                            Nome
                            <input
                              type="text"
                              value={field.name}
                              maxLength={256}
                              onChange={(e) =>
                                updateField(index, 'name', e.target.value)
                              }
                            />
                          </label>
                          <label>
                            Valor
                            <textarea
                              rows={3}
                              value={field.value}
                              maxLength={1024}
                              onChange={(e) =>
                                updateField(index, 'value', e.target.value)
                              }
                            />
                          </label>
                          <label className="admin-switch">
                            <span>Inline (lado a lado)</span>
                            <input
                              type="checkbox"
                              checked={Boolean(field.inline)}
                              onChange={(e) =>
                                updateField(index, 'inline', e.target.checked)
                              }
                            />
                          </label>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="admin-btn admin-btn--ghost admin-btn--sm"
                      onClick={addField}
                      disabled={(embed.fields || []).length >= 25}
                    >
                      <FaPlus aria-hidden="true" /> Adicionar campo
                    </button>
                  </div>
                </details>
              </div>
            )}

            {section === 'botoes' && (
              <div className="welcome-section-pane">
                <p className="admin-field-hint welcome-hint">
                  Até {buttonLimit} botões (limite do Discord por linha). Sem URL
                  = resposta ephemeral; com URL = botão de link.
                </p>

                <div className="welcome-btn-tabs">
                  {buttons.map((btn, index) => (
                    <button
                      key={btn.id}
                      type="button"
                      className={`welcome-btn-tab${selectedId === btn.id ? ' is-active' : ''}${btn.enabled ? '' : ' is-off'}`}
                      onClick={() => setActiveButtonId(btn.id)}
                    >
                      {btn.emoji ? `${btn.emoji} ` : ''}
                      {btn.label || `Botão ${index + 1}`}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="welcome-btn-tab welcome-btn-tab--add"
                    onClick={addButton}
                    disabled={buttons.length >= buttonLimit}
                    title={
                      buttons.length >= buttonLimit
                        ? `Máximo de ${buttonLimit} botões`
                        : 'Adicionar botão'
                    }
                  >
                    <FaPlus aria-hidden="true" /> Adicionar
                  </button>
                </div>

                {selectedButton && selectedIndex >= 0 ? (
                  <div className="welcome-btn-editor">
                    <div className="welcome-btn-editor__toolbar">
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        onClick={() => moveButton(selectedIndex, -1)}
                        disabled={selectedIndex <= 0}
                        title="Mover para cima"
                      >
                        <FaArrowUp aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        onClick={() => moveButton(selectedIndex, 1)}
                        disabled={selectedIndex >= buttons.length - 1}
                        title="Mover para baixo"
                      >
                        <FaArrowDown aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn--danger admin-btn--sm"
                        onClick={() => removeButton(selectedIndex)}
                      >
                        <FaTrash aria-hidden="true" /> Remover
                      </button>
                    </div>

                    <label className="admin-switch">
                      <span>
                        <strong>Botão visível</strong>
                        <small>
                          Desligado = oculto na mensagem (sem apagar).
                        </small>
                      </span>
                      <input
                        type="checkbox"
                        checked={Boolean(selectedButton.enabled)}
                        onChange={(e) =>
                          updateButtonAt(selectedIndex, {
                            enabled: e.target.checked,
                          })
                        }
                      />
                    </label>
                    <div className="welcome-grid-2">
                      <label>
                        Texto do botão
                        <input
                          type="text"
                          value={selectedButton.label || ''}
                          maxLength={80}
                          onChange={(e) =>
                            updateButtonAt(selectedIndex, {
                              label: e.target.value,
                            })
                          }
                        />
                      </label>
                      <label>
                        Emoji
                        <input
                          type="text"
                          value={selectedButton.emoji || ''}
                          maxLength={8}
                          onChange={(e) =>
                            updateButtonAt(selectedIndex, {
                              emoji: e.target.value,
                            })
                          }
                          placeholder="📖"
                        />
                      </label>
                    </div>
                    <div className="welcome-grid-2">
                      <label>
                        Estilo
                        <select
                          value={selectedButton.style || 'Primary'}
                          onChange={(e) =>
                            updateButtonAt(selectedIndex, {
                              style: e.target.value,
                            })
                          }
                          disabled={Boolean(
                            String(selectedButton.url || '').trim()
                          )}
                        >
                          {BUTTON_STYLES.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        URL (opcional — vira botão Link)
                        <input
                          type="url"
                          value={selectedButton.url || ''}
                          onChange={(e) =>
                            updateButtonAt(selectedIndex, {
                              url: e.target.value,
                            })
                          }
                          placeholder="https://… (vazio = resposta ephemeral)"
                        />
                      </label>
                    </div>
                    {!String(selectedButton.url || '').trim() && (
                      <>
                        <label>
                          Título da resposta
                          <input
                            type="text"
                            value={selectedButton.replyTitle || ''}
                            maxLength={256}
                            onChange={(e) =>
                              updateButtonAt(selectedIndex, {
                                replyTitle: e.target.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          Conteúdo da resposta (ephemeral)
                          <textarea
                            rows={5}
                            value={selectedButton.replyDescription || ''}
                            maxLength={4000}
                            onChange={(e) =>
                              updateButtonAt(selectedIndex, {
                                replyDescription: e.target.value,
                              })
                            }
                          />
                          <div className="welcome-ph-row">
                            {placeholderChips.slice(0, 5).map((ph) => (
                              <button
                                key={`btn-ph-${ph.token}`}
                                type="button"
                                className="welcome-ph welcome-ph--mini"
                                onClick={() =>
                                  insertPlaceholder(ph.token, 'btnReply')
                                }
                              >
                                {ph.token}
                              </button>
                            ))}
                          </div>
                        </label>
                        <label>
                          Cor da resposta
                          <div className="welcome-color">
                            <input
                              type="color"
                              value={
                                /^#[0-9A-Fa-f]{6}$/.test(
                                  selectedButton.replyColor || ''
                                )
                                  ? selectedButton.replyColor
                                  : '#5865F2'
                              }
                              onChange={(e) =>
                                updateButtonAt(selectedIndex, {
                                  replyColor: e.target.value,
                                })
                              }
                            />
                            <input
                              type="text"
                              value={selectedButton.replyColor || ''}
                              onChange={(e) =>
                                updateButtonAt(selectedIndex, {
                                  replyColor: e.target.value,
                                })
                              }
                            />
                          </div>
                        </label>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="admin-field-hint">
                    Nenhum botão. Clique em <strong>Adicionar</strong> para
                    criar um (máx. {buttonLimit}).
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="welcome-editor__footer">
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              onClick={onSave}
              disabled={saving || !canEdit}
            >
              {saving
                ? 'Salvando…'
                : canEdit
                  ? 'Salvar boas-vindas'
                  : 'Somente leitura'}
            </button>
          </div>
        </div>

        <aside className="welcome-preview">
          <div className="welcome-preview__card">
            <div className="welcome-preview__head">
              <h2 className="welcome-preview-title">Preview ao vivo</h2>
              <p className="welcome-preview-sub">
                Como a mensagem aparece no Discord (placeholders de exemplo)
              </p>
            </div>
            <DiscordEmbedPreview
              embed={embed}
              buttons={buttons}
              mentionUser={Boolean(settings.mentionUser)}
            />
          </div>
        </aside>
      </div>
    </section>
  );
};

export default WelcomeSettingsPanel;
